import type { PollRead } from '@models/api/poll';
import { POLL_COPY } from './pollCopy';

const MINUTE_MS = 60_000;
const HOUR_MINUTES = 60;
const DAY_MINUTES = 24 * HOUR_MINUTES;

/** An option's share of the people who answered, rounded (several answers can sum past 100%). */
export const pollShare = (votes: number | null, voters: number | null): number =>
  votes !== null && voters !== null && voters > 0 ? Math.round((votes / voters) * 100) : 0;

/** Every leading option (ties included); none while nobody is counted or counts are hidden. */
export const pollLeaderIds = (poll: {
  options: readonly { id: string; votes_count: number | null }[];
}): Set<string> => {
  const top = Math.max(0, ...poll.options.map(o => o.votes_count ?? 0));
  if (top === 0) return new Set();
  return new Set(poll.options.filter(o => o.votes_count === top).map(o => o.id));
};

/** P7: a multi-answer poll where the picks outnumber the people. */
export const pollSomePickedMoreThanOne = (
  poll: Pick<PollRead, 'allows_multiple' | 'options' | 'voters_count'>,
): boolean => {
  const voters = poll.voters_count;
  if (!poll.allows_multiple || voters === null || voters === 0) return false;
  const picks = poll.options.reduce((sum, o) => sum + (o.votes_count ?? 0), 0);
  return picks > voters;
};

// Named here, not by the locale: en-GB's short month is "Sept" in current ICU data, and the ticket
// writes "Sat 1 Nov" style three-letter names throughout.
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** "Sat 1 Nov": the calendar day in `timeZone`, from its numeric parts only. */
const dayParts = (date: Date, timeZone: string | undefined): string => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone,
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(p => p.type === type)?.value ?? 0);
  const year = part('year');
  const month = part('month');
  const day = part('day');
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return `${WEEKDAYS[weekday]} ${day} ${MONTHS[month - 1]}`;
};

const timeOfDay = (date: Date, timeZone: string | undefined): string =>
  new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).format(date);

export interface PollTime {
  text: string;
  /** The footer's clock icon; hidden beside "No end time". */
  showClock: boolean;
}

/**
 * The footer's time (TECH-197 § Time and ending): "No end time"; by slider "Ends in 3 days",
 * "Ends in 5 h 12 min", last hour "Ends in 12 min"; by date "Ends Sat 8 Nov, 18:00" in the viewer's
 * zone, then the same countdown in the last hour; ended "Ended Sat 1 Nov". Display only: the server
 * decides when a poll is closed, so a poll past its time reads "Ends in 1 min" until the re-read
 * lands. `timeZone` is for tests; the viewer's zone otherwise.
 */
export const formatPollTime = (
  poll: Pick<PollRead, 'closes_at' | 'closed_at' | 'end_set_as' | 'is_closed'>,
  nowMs: number,
  timeZone?: string,
): PollTime => {
  if (poll.is_closed) {
    const endedAt = poll.closed_at ?? poll.closes_at;
    const day = dayParts(endedAt ? new Date(endedAt) : new Date(nowMs), timeZone);
    return { text: POLL_COPY.endedOn(day), showClock: true };
  }
  if (!poll.closes_at) return { text: POLL_COPY.noEndTime, showClock: false };

  const closesAt = new Date(poll.closes_at);
  const minutesLeft = Math.max(1, Math.ceil((closesAt.getTime() - nowMs) / MINUTE_MS));
  if (minutesLeft < HOUR_MINUTES) {
    return { text: POLL_COPY.endsInMinutes(minutesLeft), showClock: true };
  }
  if (poll.end_set_as === 'date') {
    const when = `${dayParts(closesAt, timeZone)}, ${timeOfDay(closesAt, timeZone)}`;
    return { text: POLL_COPY.endsAt(when), showClock: true };
  }
  if (minutesLeft < DAY_MINUTES) {
    const hours = Math.floor(minutesLeft / HOUR_MINUTES);
    return {
      text: POLL_COPY.endsInHoursMinutes(hours, minutesLeft % HOUR_MINUTES),
      showClock: true,
    };
  }
  return { text: POLL_COPY.endsInDays(Math.floor(minutesLeft / DAY_MINUTES)), showClock: true };
};

/** The calendar day of `date` in the viewer's zone, as a sortable number (yyyymmdd). */
const dayNumber = (date: Date, timeZone: string | undefined): number => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone,
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(p => p.type === type)?.value ?? 0);
  return Date.UTC(part('year'), part('month') - 1, part('day'));
};

/**
 * A moment as the duration field shows it (TECH-197 A3, E14b): "Tomorrow, 00:40" for the next day,
 * "Today, 18:00" for this one, else "Fri 31 Oct, 22:40". In the viewer's zone.
 */
export const formatPollMoment = (date: Date, nowMs: number, timeZone?: string): string => {
  const time = timeOfDay(date, timeZone);
  const days = Math.round(
    (dayNumber(date, timeZone) - dayNumber(new Date(nowMs), timeZone)) / (DAY_MINUTES * MINUTE_MS),
  );
  if (days === 0) return `${POLL_COPY.today}, ${time}`;
  if (days === 1) return `${POLL_COPY.tomorrow}, ${time}`;
  return `${dayParts(date, timeZone)}, ${time}`;
};

/** "Sat 8 Nov, 18:00": a picked end (A3b), always with its date. */
export const formatPollDate = (date: Date, timeZone?: string): string =>
  `${dayParts(date, timeZone)}, ${timeOfDay(date, timeZone)}`;
